import React, { useState, useEffect } from 'react';
import ct from 'countries-and-timezones';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { LoadingSpinner, Button, FormInput, FormSelect } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { useNotification } from '../context/NotificationContext.jsx';

// Simple Card Component
const ConfigCard = ({ title, icon, children, actions }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    <DynamicIcon name={icon} className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            </div>
            {actions}
        </div>
        <div className="p-6 space-y-4">
            {children}
        </div>
    </div>
);

const FeatureToggle = ({ label, checked, onChange, description }) => (
    <div className="flex items-center justify-between py-3 border-b dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 px-2 rounded -mx-2 transition-colors">
        <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">{label}</p>
            {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
        <button
            onClick={onChange}
            type="button"
            className={`${checked ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-600'}
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
            role="switch"
            aria-checked={checked}
        >
            <span
                aria-hidden="true"
                className={`${checked ? 'translate-x-5' : 'translate-x-0'}
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
            />
        </button>
    </div>
);

const TIMEZONE_OPTIONS = Object.values(ct.getAllTimezones())
    .filter(tz => !tz.aliasOf)
    .sort((a, b) => a.utcOffset - b.utcOffset)
    .map(tz => ({
        value: tz.name,
        label: `(UTC${tz.utcOffsetStr}) ${tz.name.replace(/_/g, ' ')}`
    }));

const ConfigurationTab = ({ tenantId, token, currentUser }) => {
    const { data: config, isLoading, error, refresh } = useData(`/tenant-onboarding/${tenantId}/full`, token);
    const { addNotification } = useNotification();
    const [saving, setSaving] = useState(null); // 'features', 'quotas', 'support', 'auth', 'ops'

    // Fetch available features dynamically
    const [availableFeatures, setAvailableFeatures] = useState([]);
    useEffect(() => {
        apiFetch("/plans/features", token)
            .then(res => setAvailableFeatures(Array.isArray(res) ? res : []))
            .catch(err => console.error("Failed to load features:", err));
    }, [token]);

    if (isLoading) return <LoadingSpinner />;
    if (error) return <div className="text-red-500">Error loading configuration: {error.message}</div>;

    const { features, quota, support, db: dbConfig, auth: authConfig } = config || {};
    const isPlatformAdmin = ['r1', 'r2'].includes(currentUser?.roleId);

    // --- SAVE HANDLERS ---

    const saveFeatures = async (newFeatures) => {
        setSaving('features');
        try {
            await apiFetch(`/tenant-onboarding/${tenantId}/features`, token, {
                method: 'POST',
                body: JSON.stringify({
                    enabled_features: newFeatures,
                    api_call_limit: features?.api_call_limit,
                    storage_limit_gb: features?.storage_limit_gb
                })
            });
            addNotification("Feature flags updated.");
            refresh();
        } catch (err) {
            addNotification(err.message, 'error');
        } finally {
            setSaving(null);
        }
    };

    const savePlatformOps = async (overrideFeatures) => {
        setSaving('ops');
        try {
            // We use the same features endpoint but specifically for the 'alerts' flag 
            // or other overrides managed by platform admins via PUT /tenants/ features
            await apiFetch(`/tenants/${tenantId}/features`, token, {
                method: 'PUT',
                body: JSON.stringify(overrideFeatures)
            });
            addNotification("Platform operations updated.");
            refresh();
        } catch (err) {
            addNotification(err.message, 'error');
        } finally {
            setSaving(null);
        }
    };

    const saveAuth = async (e) => {
        e.preventDefault();
        setSaving('auth');
        const formData = new FormData(e.target);
        const payload = {
            mfa_enabled: formData.get('mfa_enabled') === 'on',
            password_min_length: parseInt(formData.get('password_min_length')) || 8,
            password_expiry_days: parseInt(formData.get('password_expiry_days')) || 90,
            session_expiry_minutes: parseInt(formData.get('session_expiry_minutes')) || 15,
            password_requires_uppercase: formData.get('password_requires_uppercase') === 'on',
            password_requires_lowercase: formData.get('password_requires_lowercase') === 'on',
            password_requires_number: formData.get('password_requires_number') === 'on',
            password_requires_special: formData.get('password_requires_special') === 'on',
            sso_provider: formData.get('sso_provider') || null,
            timezone: formData.get('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        try {
            await apiFetch(`/tenant-onboarding/${tenantId}/auth`, token, { method: 'POST', body: JSON.stringify(payload) });
            addNotification("Authentication settings updated.");
            refresh();
        } catch (err) {
            addNotification(err.message, 'error');
        } finally {
            setSaving(null);
        }
    };

    const saveQuotas = async (e) => {
        e.preventDefault();
        setSaving('quotas');
        const formData = new FormData(e.target);
        const payload = {
            api_call_limit: parseInt(formData.get('api_call_limit')) || 0,
            user_limit: parseInt(formData.get('user_limit')) || 0,
        };
        try {
            await apiFetch(`/tenant-onboarding/${tenantId}/quotas`, token, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            addNotification("Resource quotas updated.");
            refresh();
        } catch (err) {
            addNotification(err.message, 'error');
        } finally {
            setSaving(null);
        }
    };

    const saveSupport = async (e) => {
        e.preventDefault();
        setSaving('support');
        const formData = new FormData(e.target);
        const payload = {
            support_tier: formData.get('support_tier'),
            sla_description: formData.get('sla_description'),
            response_time_hours: parseInt(formData.get('response_time_hours')) || 24,
        };
        try {
            await apiFetch(`/tenant-onboarding/${tenantId}/support`, token, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            addNotification("Support configuration updated.");
            refresh();
        } catch (err) {
            addNotification(err.message, 'error');
        } finally {
            setSaving(null);
        }
    };

    // --- RENDER HELPERS ---

    const toggleFeature = (key) => {
        const current = features?.enabled_features || {};
        const newState = { ...current, [key]: !current[key] };
        saveFeatures(newState);
    };

    const toggleAlerts = () => {
        const current = features?.enabled_features || {};
        // Default is TRUE if missing. So if undefined, toggling means setting to FALSE.
        // If present, toggle value.
        const currentVal = current.alerts !== false; // effectively true
        const newVal = !currentVal;

        savePlatformOps({ alerts: newVal });
    };

    const toggleSingleSession = () => {
        const current = features?.enabled_features || {};
        const currentVal = current.single_session === true;
        savePlatformOps({ single_session: !currentVal });
    };

    // Extra features that must always appear in the list, regardless of what /plans/features returns.
    // These are added to the DB via seed but may be missing in older environments.
    const REQUIRED_FEATURES = [
        { featureId: 'ai_insight', featureName: 'AI Insights', featureDescription: 'Ask questions and generate insights from data using natural language' },
    ];

    // Merge: start from API result (or fallback list), then append any REQUIRED_FEATURES not already present
    const baseFeatures = availableFeatures.length > 0
        ? availableFeatures
        : [
            { featureId: 'dashboard', featureName: 'Dashboard Access', featureDescription: 'Allow users to view dashboards' },
            { featureId: 'ml_engine', featureName: 'ML Engine', featureDescription: 'Enable machine learning execution' },
            { featureId: 'genai', featureName: 'GenAI Studio', featureDescription: 'Access to LLM tools' },
            { featureId: 'marketplace', featureName: 'App Marketplace', featureDescription: 'Install new apps' },
        ];

    const existingIds = new Set(baseFeatures.map(f => f.code || f.featureId));
    const displayFeatures = [
        ...baseFeatures,
        ...REQUIRED_FEATURES.filter(f => !existingIds.has(f.featureId)),
    ];

    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 pb-20">
            {/* 1. FEATURES CARD */}
            <ConfigCard title="Feature Flags" icon="ToggleRight">
                <div className="space-y-1">
                    {displayFeatures.map(item => {
                        const id = item.code || item.featureId;
                        const name = item.name || item.featureName;
                        const desc = item.description || item.featureDescription;
                        return (
                            <FeatureToggle
                                key={id}
                                label={name}
                                description={desc}
                                checked={features?.enabled_features?.[id] || false}
                                onChange={() => toggleFeature(id)}
                            />
                        );
                    })}
                </div>
            </ConfigCard>

            {/* 1.5 AUTHENTICATION & SECURITY */}
            <ConfigCard title="Authentication & Security" icon="Lock"
                actions={
                    <Button size="sm" form="authForm" type="submit" isLoading={saving === 'auth'}>
                        Save Auth
                    </Button>
                }
            >
                <div className="mb-6 pb-6 border-b border-gray-100 dark:border-gray-700">
                    <FeatureToggle
                        label="Enforce Single Session"
                        description="If enabled, logging in from a new device will automatically invalidate previous sessions."
                        checked={features?.enabled_features?.single_session === true}
                        onChange={toggleSingleSession}
                    />
                </div>

                <form id="authForm" onSubmit={saveAuth} className="space-y-4">
                    <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-700">
                        <div>
                            <label htmlFor="mfa_enabled" className="block text-sm font-medium text-gray-900 dark:text-gray-100">Multi-Factor Authentication</label>
                            <p className="text-xs text-gray-500">Require all users to setup 2FA.</p>
                        </div>
                        <div className="flex items-center">
                            <input type="checkbox" name="mfa_enabled" id="mfa_enabled" defaultChecked={authConfig?.mfa_enabled} className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary toggle-checkbox" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormInput label="Min Password Length" name="password_min_length" type="number" defaultValue={authConfig?.password_min_length || 8} />
                        <FormInput label="Password Expiry (Days)" name="password_expiry_days" type="number" defaultValue={authConfig?.password_expiry_days || 90} />
                    </div>

                    <div className="pt-2 pb-2">
                        <p className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Password Requirements</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="flex items-center gap-2">
                                <input type="checkbox" name="password_requires_uppercase" id="password_requires_uppercase" defaultChecked={authConfig?.password_requires_uppercase !== false} className="rounded border-gray-300 text-primary focus:ring-primary" />
                                <label htmlFor="password_requires_uppercase" className="text-sm text-gray-700 dark:text-gray-300">Uppercase</label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" name="password_requires_lowercase" id="password_requires_lowercase" defaultChecked={authConfig?.password_requires_lowercase !== false} className="rounded border-gray-300 text-primary focus:ring-primary" />
                                <label htmlFor="password_requires_lowercase" className="text-sm text-gray-700 dark:text-gray-300">Lowercase</label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" name="password_requires_number" id="password_requires_number" defaultChecked={authConfig?.password_requires_number !== false} className="rounded border-gray-300 text-primary focus:ring-primary" />
                                <label htmlFor="password_requires_number" className="text-sm text-gray-700 dark:text-gray-300">Number</label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" name="password_requires_special" id="password_requires_special" defaultChecked={authConfig?.password_requires_special !== false} className="rounded border-gray-300 text-primary focus:ring-primary" />
                                <label htmlFor="password_requires_special" className="text-sm text-gray-700 dark:text-gray-300">Special Char</label>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormInput label="Session Expiry (Minutes)" name="session_expiry_minutes" type="number" defaultValue={authConfig?.session_expiry_minutes || 15} />
                    </div>

                    <FormSelect 
                        label="Timezone" 
                        name="timezone" 
                        defaultValue={authConfig?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                    >
                        {TIMEZONE_OPTIONS.map(tz => (
                            <option key={tz.value} value={tz.value}>
                                {tz.label}
                            </option>
                        ))}
                    </FormSelect>
                </form>
            </ConfigCard>

            {/* 2. QUOTAS CARD */}
            <ConfigCard title="Resource Quotas" icon="BarChart3"
                actions={
                    <Button size="sm" form="quotaForm" type="submit" isLoading={saving === 'quotas'}>
                        Save Quotas
                    </Button>
                }
            >
                <form id="quotaForm" onSubmit={saveQuotas} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput label="Monthly Credits" name="api_call_limit" type="number" defaultValue={quota?.api_call_limit} placeholder="e.g. 50000" />
                    <FormInput label="Max Users (Concurrent)" name="user_limit" type="number" defaultValue={quota?.user_limit} placeholder="e.g. 25" />
                </form>
            </ConfigCard>

            {/* 3. SUPPORT CARD */}
            <ConfigCard title="Support & SLA" icon="LifeBuoy"
                actions={
                    <Button size="sm" form="supportForm" type="submit" isLoading={saving === 'support'}>
                        Update Support
                    </Button>
                }
            >
                <form id="supportForm" onSubmit={saveSupport} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormSelect label="Support Tier" name="support_tier" defaultValue={support?.support_tier}>
                            <option value="Basic">Basic</option>
                            <option value="Silver">Silver</option>
                            <option value="Gold">Gold</option>
                            <option value="Platinum">Platinum</option>
                        </FormSelect>
                        <FormInput label="Response Time (Hours)" name="response_time_hours" type="number" defaultValue={support?.response_time_hours} />
                    </div>
                    <FormInput label="SLA Description" name="sla_description" defaultValue={support?.sla_description} />
                </form>
            </ConfigCard>

        </div>
    );
};

export default ConfigurationTab;
